import React, { useState, useEffect, useRef } from 'react';
import { useNotificaciones } from '../context/NotificacionesContext';
import { Modal } from './ui/Modal';

// Modal que se muestra automáticamente al loguearse si hay notificaciones sin leer.
// Cada notificación se marca como leída con su propio checkbox (igual que en el
// indicador). Se cierra con la X. Si el usuario cierra sin marcarlas, no vuelve a
// mostrarse hasta el próximo login (nueva sesión).
export function PostLoginNotificacionesModal() {
    const { notificaciones, noLeidas, loading, marcarComoLeidas } = useNotificaciones();
    const [mostrado, setMostrado] = useState(false);
    const [marcandoId, setMarcandoId] = useState(null);
    const [detalle, setDetalle] = useState(null);
    // Marca que el modal ya se abrió automáticamente esta sesión: así la X lo cierra
    // definitivamente y no se vuelve a abrir (aunque queden no leídas).
    const yaAutoAbierto = useRef(false);

    useEffect(() => {
        if (loading) return;
        // Abrir el modal UNA sola vez por montaje, recién cuando termina la carga
        // inicial (para no usar los valores iniciales del hook), si hay no leídas.
        if (!yaAutoAbierto.current && noLeidas > 0) {
            yaAutoAbierto.current = true;
            setMostrado(true);
        }
    }, [loading, noLeidas]);

    const handleMarcar = async (notificacionId) => {
        if (marcandoId !== null) return;
        setMarcandoId(notificacionId);
        await marcarComoLeidas([notificacionId]);
        setMarcandoId(null);
    };

    // Como el modal principal, pero además cierra el modal ampliado al marcar.
    const handleMarcarDetalle = async (notificacionId) => {
        if (marcandoId !== null) return;
        setMarcandoId(notificacionId);
        await marcarComoLeidas([notificacionId]);
        setMarcandoId(null);
        setDetalle(null);
    };

    // La X cierra SIEMPRE todos los modales (el ampliado y el principal post-login).
    const cerrarTodos = () => {
        setDetalle(null);
        setMostrado(false);
    };

    const noLeidasList = notificaciones.filter(n => !n.leida);

    // Objeto de la notificación ampliada derivado del hook (siempre actualizado),
    // para que el checkbox refleje el cambio al marcarla como leída.
    const detalleActual = detalle
        ? (notificaciones.find(x => Number(x.notificacion_id) === Number(detalle.notificacion_id)) || detalle)
        : null;

    return (
        <>
            <Modal
                isOpen={mostrado && noLeidasList.length > 0}
                onClose={cerrarTodos}
                title="Notificaciones"
                maxWidth="max-w-lg"
            >
            <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    Tenés {noLeidasList.length} notificación{noLeidasList.length !== 1 ? 'es' : ''} pendiente{noLeidasList.length !== 1 ? 's' : ''} de lectura:
                </p>

                <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-100 dark:divide-gray-800">
                    {noLeidasList.map(n => (
                        <div key={n.notificacion_id} className="p-3 bg-gray-50 dark:bg-gray-800">
                            <div className="flex items-start gap-1">
                                {/* Botón para expandir / ver el detalle completo */}
                                <button
                                    onClick={() => setDetalle(n)}
                                    className="flex-shrink-0 mt-0.5 p-1 rounded-md text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
                                    title="Ver notificación completa"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                    </svg>
                                </button>
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 flex-1">{n.titulo}</p>
                            </div>
                            {n.descripcion && (
                                <div className="pl-1">
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-3 whitespace-pre-wrap">
                                        {n.descripcion}
                                    </p>
                                    {/* Ver el texto completo si es extenso */}
                                    <button
                                        onClick={() => setDetalle(n)}
                                        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline mt-1"
                                        title="Leer descripción completa"
                                    >
                                        Ver completo
                                    </button>
                                </div>
                            )}
                            {n.imagen && (
                                <div className="relative mt-2 pl-1">
                                    <img
                                        src={n.imagen}
                                        alt={n.titulo}
                                        className="max-h-24 rounded-md border border-gray-200 dark:border-gray-700 object-cover"
                                    />
                                    {/* Botón para expandir la imagen */}
                                    <button
                                        onClick={() => setDetalle(n)}
                                        className="absolute bottom-2 left-2 p-1 rounded-md text-white bg-black/50 hover:bg-black/70 transition-colors"
                                        title="Ver imagen ampliada"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Marcar como leídas (debajo de las notificaciones) */}
                {noLeidasList.map(n => (
                    <div key={`check-${n.notificacion_id}`} className="flex items-center justify-end">
                        <button
                            onClick={() => handleMarcar(n.notificacion_id)}
                            disabled={marcandoId !== null}
                            title="Marcar como leída"
                            className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer"
                        >
                            <span className="flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors border-gray-400 dark:border-gray-500">
                                {n.leida && (
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </span>
                            Marcar leída
                        </button>
                    </div>
                ))}

                <p className="text-xs text-gray-400 dark:text-gray-500">
                    Si cerrás sin marcarlas, se volverán a mostrar en tu próximo ingreso.
                </p>
            </div>
        </Modal>

        {/* Modal de detalle con la imagen ampliada */}
        <Modal
            isOpen={!!detalle}
            onClose={cerrarTodos}
            title={detalleActual?.titulo}
            maxWidth="max-w-3xl"
        >
            {detalleActual && (
                <div className="space-y-3">
                    {detalleActual.descripcion && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{detalleActual.descripcion}</p>
                    )}
                    {detalleActual.imagen && (
                        <img
                            src={detalleActual.imagen}
                            alt={detalleActual.titulo}
                            className="w-full rounded-md border border-gray-200 dark:border-gray-700"
                        />
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(detalleActual.fecha_creacion).toLocaleString('es-AR')}
                    </p>
                    {/* Marcar como leída */}
                    <div className="flex items-center justify-end border-t border-gray-200 dark:border-gray-700 pt-3">
                        <button
                            onClick={() => detalleActual.notificacion_id && handleMarcarDetalle(detalleActual.notificacion_id)}
                            disabled={marcandoId !== null}
                            title="Marcar como leída"
                            className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer"
                        >
                            <span className="flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors border-gray-400 dark:border-gray-500">
                                {detalleActual.leida && (
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </span>
                            Marcar leída
                        </button>
                    </div>
                </div>
            )}
            </Modal>
        </>
    );
}
